import React from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@material-ui/core/styles';
import moment from 'moment';
import 'moment/locale/pt-br';

/**
 * @function EvolutionChart
 * @description Renders a time-series line chart with an optional trend (regression) line.
 * Uses Chart.js and supports localization for English and Portuguese.
 * @param {Object} props - Component properties.
 * @param {number} [props.width=500] - Chart width in pixels.
 * @param {number} [props.height=300] - Chart height in pixels.
 * @param {Array<number>} [props.regression=[]] - Regression (trend) line values.
 * @param {Array<string|Date|number>} [props.x=[]] - X-axis values (dates or numbers).
 * @param {Array<number>} [props.y=[]] - Y-axis values for the main dataset.
 * @param {string} [props.xLabel="x"] - Label for the X-axis.
 * @param {string} [props.yLabel="y"] - Label for the Y-axis.
 * @param {string} [props.trendLabel="trend"] - Label for the regression dataset.
 * @param {number} [props.yMin] - Minimum value for the Y-axis.
 * @param {number} [props.yMax] - Maximum value for the Y-axis.
 * @see {@link createData} - Builds the chart dataset.
 * @see {@link createOptions} - Builds the chart options.
 * **Called by:
 * @see {TransectEvolution} - to render each transect's evolution chart.
 * @memberof module:Shoreline
 * @returns {JSX.Element} Line chart element.
 */
const EvolutionChart = ({
  width = 500,
  height = 300,
  regression = [],
  x = [],
  y = [],
  xLabel = 'x',
  yLabel = 'y',
  trendLabel = 'trend',
  yMin,
  yMax,
}) => {
  const [t] = useTranslation();
  const theme = useTheme();
  const gridColor = theme.palette.divider;
  const labelColor = theme.palette.text.secondary;

  const chartLang = t('forms.transectEvolution.chartLanguage');
  const charLanguage = chartLang.toLowerCase().includes('portugu');
  moment.locale(charLanguage ? 'pt-BR' : 'en');

  /**
   * @function createData
   * @description Builds the dataset for the chart, pairing the main y-values series with the
   * regression (trend) series.
   * **Called by:
   * @see {EvolutionChart} - via the Line component's data prop.
   * @memberof module:Shoreline
   * @returns {Object} Chart.js dataset configuration.
   */
  const createData = () => ({
    labels: x,
    datasets: [
      {
        pointRadius: 5,
        lineTension: 0,
        borderColor: '#009688',
        backgroundColor: '#009688',
        fill: false,
        data: y,
        label: yLabel,
      },
      {
        pointRadius: 0,
        lineTension: 0,
        borderColor: '#0d88c5',
        backgroundColor: '  #0d88c5',
        borderWidth: 1.5,
        fill: false,
        borderDash: [10, 10],
        data: regression,
        label: trendLabel,
      },
    ],
  });

  /**
   * @function createOptions
   * @description Builds the chart options (axes, tooltips, labels).
   * 1. X-axis uses a time scale (month unit); tooltip date format is "DD/MM/YYYY" for pt-BR
   *    or "MM/DD/YYYY" for en.
   * 2. Y-axis ticks and tooltip values use a comma as the decimal separator for pt-BR, or a
   *    dot for en.
   * **Called by:
   * @see {EvolutionChart} - via the Line component's options prop.
   * @memberof module:Shoreline
   * @returns {Object} Chart.js options configuration.
   */
  const createOptions = () => ({
    scales: {
      xAxes: [
        {
          type: 'time',
          time: {
            unit: 'month',
            displayFormats: {
              month: 'MMM YYYY',
            },
            tooltipFormat: charLanguage ? 'DD/MM/YYYY' : 'MM/DD/YYYY',
          },
          scaleLabel: {
            display: true,
            labelString: xLabel,
            fontColor: labelColor,
          },
          ticks: { fontColor: labelColor },
          gridLines: { color: gridColor, zeroLineColor: gridColor },
        },
      ],
      yAxes: [
        {
          ticks: {
            min: yMin,
            max: yMax,
            fontColor: labelColor,
            callback: function (value) {
              return charLanguage ? value.toFixed(2).replace('.', ',') : value.toFixed(2);
            },
          },
          scaleLabel: {
            display: true,
            labelString: yLabel,
            fontColor: labelColor,
          },
          gridLines: { color: gridColor, zeroLineColor: gridColor },
        },
      ],
    },
    legend: {
      labels: { fontColor: labelColor },
    },
    tooltips: {
      callbacks: {
        label: function (tooltipItem, data) {
          let label = data.datasets[tooltipItem.datasetIndex].label || '';
          if (label) label += ': ';
          let val = parseFloat(tooltipItem.yLabel).toFixed(2);
          if (charLanguage) {
            val = val.replace('.', ',');
          }
          return label + val;
        },
      },
    },
  });

  return (
    <div>
      <Line data={createData()} options={createOptions()} width={width} height={height} />
    </div>
  );
};

export default EvolutionChart;
